<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { Landmark } from 'lucide-vue-next'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import { Button } from 'pejer/src/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import DialogFooter from 'pejer/src/components/ui/dialog/DialogFooter.vue'
import { Input } from 'pejer/src/components/ui/input'
import { Label } from 'pejer/src/components/ui/label'
import usePejerMutation from 'pejer/src/usePejerMutation'
import usePejerQuery from 'pejer/src/usePejerQuery'
import { computed, ref, watch } from 'vue'
import ClubAccountHistoryList from './ClubAccountHistoryList.vue'
import { ClubAccountItem, formatAccount, formatKroner, toOere } from './types'

const props = defineProps<{
  item: ClubAccountItem
}>()

const isOpen = ref(false)
const pejer = usePejer()

const amount = ref<string | number>('')
const toAccount = ref('')
const note = ref('')
const confirmed = ref(false)

const { data: history, isLoading: historyLoading } = usePejerQuery(
  pejer.urls.payments['club-account-history'],
  {
    pathArgs: { id: props.item.paymentReferenceID },
    queryKey: ['club-account-history', props.item.paymentReferenceID],
    enabled: isOpen,
  }
)

const payersWithAccount = computed(() =>
  (history.value?.payers ?? []).filter((p) => !!p.fromBankAccount)
)

watch(isOpen, (open) => {
  if (!open) return
  amount.value = props.item.paidKroner
  toAccount.value = ''
  note.value = ''
  confirmed.value = false
})

// Suggest the account that paid the most, once the payers are loaded.
watch(payersWithAccount, (list) => {
  if (isOpen.value && !toAccount.value && list.length > 0) toAccount.value = list[0].fromBankAccount!
})

const amountInOere = computed(() => toOere(amount.value))
const toAccountDigits = computed(() => toAccount.value.replace(/\D/g, ''))
const isValid = computed(
  () =>
    amountInOere.value >= 1 &&
    amountInOere.value <= toOere(props.item.paidKroner) &&
    toAccountDigits.value.length === 11 &&
    confirmed.value
)

const { mutate, dataState } = usePejerMutation(
  pejer.urls.payments['club-account-register-refund'],
  {
    queryKeys: [['payments'], ['invoices'], ['refundRequests'], ['club-account-history']],
    onSuccess: () => {
      pejer.postToParent('payment.refunded', { paymentReferenceId: props.item.paymentReferenceID })
      isOpen.value = false
    },
  },
  { toastOnSuccess: { title: 'Tilbakebetalingen er registrert' } }
)

function submit() {
  if (!isValid.value) return
  mutate({
    pathArgs: { id: props.item.paymentReferenceID },
    body: {
      amount: amountInOere.value,
      toBankAccountNumber: toAccountDigits.value,
      note: note.value || null,
    },
  })
}
</script>
<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogContent class="max-w-[calc(100vw-20px)] max-h-[calc(100vh-50px)] flex flex-col">
      <DialogTitle class="flex flex-col gap-1">
        <span class="text-muted-foreground text-sm">{{ item.customerName }}</span>
        <span class="text-lg font-semibold">Registrer tilbakebetaling</span>
      </DialogTitle>
      <DialogDescription class="flex items-start gap-2 text-sm text-slate-600">
        <Landmark :size="16" class="shrink-0 mt-0.5" />
        <span>
          Pengene står på klubbens egen konto, så PayBucky kan ikke betale dem tilbake.
          <b>Overfør først beløpet fra klubbens nettbank</b>, og registrer det deretter her.
          Fakturaen oppdateres, og mottakeren får en kreditnota.
        </span>
      </DialogDescription>
      <div class="flex flex-col gap-4 py-2 text-sm">
        <div class="text-slate-600">
          {{ item.description }} · Innbetalt {{ formatKroner(item.paidKroner) }}
          <span v-if="item.kid"> · KID {{ item.kid }}</span>
        </div>

        <ClubAccountHistoryList :history="history" :is-loading="historyLoading" />

        <div class="flex flex-col gap-2">
          <Label>Betal tilbake til</Label>
          <span v-if="historyLoading" class="text-slate-500">Henter innbetalinger …</span>
          <span v-else-if="payersWithAccount.length === 0" class="text-slate-500">
            Fant ingen innbetalinger med kontonummer. Finn betalerens konto i nettbanken ved å søke
            på KID-en.
          </span>
          <label
            v-for="payer in payersWithAccount"
            :key="payer.fromBankAccount!"
            class="flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-slate-50"
          >
            <input
              v-model="toAccount"
              type="radio"
              name="register-refund-payer"
              :value="payer.fromBankAccount"
            />
            <span class="font-mono">{{ formatAccount(payer.fromBankAccount!) }}</span>
            <span class="text-slate-600">· {{ formatKroner(payer.amount / 100) }}</span>
            <span v-if="payer.payments > 1" class="text-slate-500">
              ({{ payer.payments }} innbetalinger)
            </span>
            <span v-if="payer.isManual" class="text-slate-500">· registrert manuelt</span>
          </label>
        </div>

        <div class="flex flex-col gap-1">
          <Label for="register-refund-to">Kontonummer pengene ble sendt til</Label>
          <Input
            id="register-refund-to"
            v-model="toAccount"
            placeholder="1234 56 78901"
            inputmode="numeric"
          />
        </div>
        <div class="flex flex-col gap-1">
          <Label for="register-refund-amount">Beløp tilbakebetalt (kr)</Label>
          <Input id="register-refund-amount" v-model="amount" type="number" step="0.01" min="0.01" />
        </div>
        <div class="flex flex-col gap-1">
          <Label for="register-refund-note">Grunn (valgfritt, vises på kreditnotaen)</Label>
          <Input id="register-refund-note" v-model="note" maxlength="200" />
        </div>
        <label class="flex items-start gap-2 cursor-pointer">
          <input v-model="confirmed" type="checkbox" class="mt-0.5" />
          <span>Jeg har overført beløpet fra klubbens nettbank.</span>
        </label>
      </div>
      <DialogFooter>
        <DialogClose as-child>
          <Button variant="ghost">Avbryt</Button>
        </DialogClose>
        <DataStateButton :data-state="dataState" :disabled="!isValid" @click.prevent.stop="submit">
          Registrer tilbakebetaling
        </DataStateButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
